/**
 * Raios do GOES-19 (GLM, o mapeador de raios do satélite) publicados pela
 * máquina do agendador (`AmaView-dados/agendador/raios`).
 *
 * A NOAA publica os flashes do GLM a cada 20 s (GLM-L2-LCFA, ~200 KB por
 * arquivo, netCDF): a máquina junta os da América do Sul em quadros de 10 min
 * (o mesmo carimbo das imagens do STAR), com a posição em centésimos de grau,
 * e guarda 7 dias. Nada é filtrado: cada flash que a NOAA publicou está no
 * quadro. A consulta por ponto (`/raios/v1/ponto`) diz se houve raio perto de
 * um foco nas horas antes dele: é ela que a ficha do foco e a denúncia usam.
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import { formatCompactUtc } from '../lib/time';
import { parseStarStamp } from '../goes/stamp';

/** Onde a máquina publica. `VITE_RAIOS_URL` aponta para outro lugar (vazio desliga a camada). */
export const BASE_RAIOS = (import.meta.env.VITE_RAIOS_URL ?? 'https://147.15.84.134/raios').replace(/\/$/, '');

export const NOAA_GLM_URL = 'https://www.star.nesdis.noaa.gov/goesr/product_land_glm.php';
export const RAIOS_ATRIBUICAO = 'Raios: NOAA — GOES-19 GLM';

/** Raio e janela da consulta por ponto (os padrões da máquina). */
export const RAIO_CONSULTA_KM = 25;
export const HORAS_CONSULTA = 72;

export interface QuadroRaios {
  /** Início do quadro (epoch ms, UTC). */
  t: number;
  /** Carimbo AAAADDDHHMM. */
  c: string;
  /** Flashes no quadro. */
  n: number;
  /** Arquivos de 20 s lidos (30 = quadro completo). */
  a: number;
}

export interface IndiceRaios {
  gerado: number | null;
  arquivosPorQuadro: number;
  quadros: QuadroRaios[];
}

export function lerIndice(cru: unknown): IndiceRaios {
  const m = cru as { gerado?: unknown; quadros?: unknown; arquivos_por_quadro?: unknown };
  if (!m || typeof m !== 'object' || !Array.isArray(m.quadros)) throw new Error('índice dos raios sem a lista `quadros`');
  const quadros: QuadroRaios[] = [];
  const vistos = new Set<number>();
  for (const q of m.quadros as Record<string, unknown>[]) {
    if (!q || typeof q.c !== 'string' || !/^\d{11}$/.test(q.c)) continue;
    const t = parseStarStamp(q.c);
    if (t === null || vistos.has(t)) continue;
    vistos.add(t);
    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.round(v) : 0);
    quadros.push({ t, c: q.c, n: num(q.n), a: num(q.a) });
  }
  quadros.sort((a, b) => a.t - b.t);
  const gerado = typeof m.gerado === 'string' ? Date.parse(m.gerado) : NaN;
  const apq = typeof m.arquivos_por_quadro === 'number' ? m.arquivos_por_quadro : 30;
  return { gerado: Number.isFinite(gerado) ? gerado : null, arquivosPorQuadro: apq, quadros };
}

/** O índice muda a cada 20 s; `no-cache` revalida no servidor. */
export async function carregarIndice(signal?: AbortSignal): Promise<IndiceRaios> {
  const cru = await fetchWithRetry<unknown>(`${BASE_RAIOS}/v1/indice.json`, { cache: 'no-cache', mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
  return lerIndice(cru);
}

/** Flashes de um quadro: [lon, lat, epoch ms] em sequência. */
export function lerQuadro(cru: unknown, t0: number): Float64Array {
  const v = (cru as { v?: unknown })?.v;
  if (!Array.isArray(v)) throw new Error('quadro dos raios sem a lista `v`');
  const out = new Float64Array(v.length * 3);
  let k = 0;
  for (const p of v as unknown[]) {
    if (!Array.isArray(p) || p.length < 3) continue;
    const [lon, lat, s] = p as number[];
    if (!Number.isFinite(lon) || !Number.isFinite(lat) || !Number.isFinite(s)) continue;
    out[k++] = lon / 100;
    out[k++] = lat / 100;
    out[k++] = t0 + s * 1000;
  }
  return out.subarray(0, k);
}

export async function carregarQuadro(q: Pick<QuadroRaios, 'c' | 't'>, signal?: AbortSignal): Promise<Float64Array> {
  const cru = await fetchWithRetry<unknown>(`${BASE_RAIOS}/v1/quadros/${q.c}.json`, { mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
  return lerQuadro(cru, q.t);
}

/** Resposta da consulta por ponto. */
export interface RaiosPerto {
  raioKm: number;
  horas: number;
  /** Fração das fatias de 20 s da janela que a máquina leu (1 = janela inteira). */
  cobertura: number;
  n: number;
  maisPertoKm: number | null;
  /** O raio mais recente da janela (epoch ms). */
  ultimo: number | null;
}

export function lerPonto(cru: unknown): RaiosPerto {
  const m = cru as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  if (num(m?.n) === null || num(m?.cobertura) === null) throw new Error('resposta dos raios fora do formato');
  const ultimo = typeof m.ultimo === 'string' ? Date.parse(m.ultimo) : NaN;
  return {
    raioKm: num(m.raio_km) ?? RAIO_CONSULTA_KM,
    horas: num(m.horas) ?? HORAS_CONSULTA,
    cobertura: num(m.cobertura)!,
    n: num(m.n)!,
    maisPertoKm: num(m.mais_perto_km),
    ultimo: Number.isFinite(ultimo) ? ultimo : null,
  };
}

const cachePonto = new Map<string, Promise<RaiosPerto>>();

/**
 * Raios a até `RAIO_CONSULTA_KM` do ponto nas `HORAS_CONSULTA` antes de `t`.
 * Uma consulta por foco na sessão; falha (ou janela ainda incompleta) pede de
 * novo na próxima vez.
 */
export function buscarRaiosPerto(lat: number, lon: number, t: number): Promise<RaiosPerto> {
  const p = new URLSearchParams({ lat: lat.toFixed(4), lon: lon.toFixed(4), t: formatCompactUtc(t), h: String(HORAS_CONSULTA), r: String(RAIO_CONSULTA_KM) });
  const url = `${BASE_RAIOS}/v1/ponto?${p}`;
  let pedido = cachePonto.get(url);
  if (!pedido) {
    pedido = fetchWithRetry(url, { mode: 'cors', credentials: 'omit' }, { retries: 1, timeoutMs: 15_000, read: async (r) => lerPonto(await r.json()) });
    pedido.then(
      (r) => r.cobertura < 0.99 && cachePonto.delete(url),
      () => cachePonto.delete(url),
    );
    cachePonto.set(url, pedido);
  }
  return pedido;
}
